import { memo, useState } from "react";
import { useSelector } from "react-redux";
import FavoriteModal from "./FavoriteModal";
import PropertyTags from "./CardComponents/PropertyTags";
import PriceAndLabel from "./CardComponents/PriceAndLabel";
import BedBathSqft from "./CardComponents/BedBathSqft";
import TitleHeader from "./CardComponents/TitleHeader";
import ImageCarousel from "./CardComponents/ImageCarousel";
import FavoriteButton from "./CardComponents/FavoriteButton";
import CountdownTimer from "./CardComponents/CountdownTimer";
import TagIcon from "./CardComponents/TagIcon";
import CardBidButton from "./CardBidButton";
import { getEventCategory, trackButtonClick } from "@/utils/analytics";
import { getSrpLocationLabel } from "@/utils/filterTrackingService";
import { navigateToProperty } from "@/utils/propertyNavigation";

// Formats auction date - displays raw time from API (backend sends time in EDT but labeled as UTC)
const formatAuctionDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

const CardItem = ({
  item,
  isGridOrList,
  isRecommendedProperties = false,
  properties,
  setProperties,
  cardIndex,
}) => {
  const { selectedProperty } = useSelector((state) => state.srp);
  const [showModal, setShowModal] = useState(false);

  const onNavigatePDP = (label) => {
    const eventCategory = getEventCategory();

    trackButtonClick({
      eventName:
        eventCategory === "NEW-SRP"
          ? `property_card_click`
          : `recommended_property_click`,
      eventCategory: eventCategory,
      eventSource:
        eventCategory === "NEW-SRP"
          ? `${label ? label : "Property card"} click`
          : `Recomended property detials click`,
      eventLabel:
        eventCategory === "NEW-SRP"
          ? `Property id: ${item?.propertyId}| Location: ${getSrpLocationLabel()}`
          : `Section position: 5 | Property position: ${
              cardIndex + 1
            } | Property id: ${item?.propertyId}`,
      eventParams: { section: label ? label : "property" },
    });
    navigateToProperty(item?.listingUrl || `/property/${item?.listingId || item?.propertyId}`, item?.category, item?.propertyStatus);
  };

  const hasValidCountdown =
    item?.listingType === "AUCN" &&
    (item?.propertyStatus === "ACTV" || item?.propertyStatus === "ACTIVE") &&
    typeof item?.auctionTimeLeft === "number"
      ? true
      : false;
  const tags = {
    firstLook: item?.firstLook === "Y",
    isFcProxyBidEnabled: item?.isFcProxyBidEnabled,
    hotProperty: item?.hotProperty === "Y" || item?.isHotProperty === "Y",
    cwcotFLP:
      item?.category === "SC" &&
      item?.cwcotUpdatesEnabled &&
      item?.cwcotUpdatesValid &&
      "CWCOT FIRST LOOK PROGRAM",
    reserveMet:
      !item?.clientCodeRestrictedFlag &&
      item?.parentBulkPropertyFlag !== "Y" &&
      item?.invrAprvlReqdProp === "N" &&
      (item?.reserveMet === "Y" || item?.reserveMetFlag)
        ? true
        : false,
    isNoBuyerPremium: item?.isNoBuyerPremium,
    isPreview: item?.isPreview || item?.propertyStatus === "PACT" ,
    commercialProperty:
      item?.commercialRetailTag === "Y" || item?.isCommercialProperty === "Y",
    signatureSeller: item?.sellerId && item?.sellerId === "501",
    selfShowing: item?.selfShowingFlag === "Y",
    priceReduced: item?.tagName === "Price Reduced",
  };
  const tagStyle = {
    firstLook: {
      title: "First Look Period",
      color: "#08AC12"
    },
    selfShowing: {
      title: "Self Showing",
      color: "#4ab440",
    },
    hotProperty: {
      title: "Hot Property",
      color: "#F25D49",
    },
    cwcotFLP: {
      title: "CWCOT FIRST LOOK PROGRAM",
      color: "#F25D49",
    },
    reserveMet: {
      title: "Reserve Met",
      color: "#F25D49",
    },
    isNoBuyerPremium: {
      title: "No Buyer’s Premium",
      color: "#118BF4",
    },
    isPreview: {
      title: "Preview Property",
      color: "#ff7900",
    },
    commercialProperty: {
      title: item?.propertyType ? item.propertyType.replace(/_/g, ' ') : 'Commercial',
      color: "#2D6BA4",
    },
    isFcProxyBidEnabled: {
      title: "Proxy Property",
      color: "#118BF4",
    },
    signatureSeller: {
      title: "Hubzu Signature Homes",
      color: "#63938E",
    },
    priceReduced: {
      title: "Price Reduced",
      color: "#FF7900",
    },
  };

  return (
    <div
      className={`${
        isRecommendedProperties
          ? "col-md-6 col-lg-6"
          : isGridOrList
          ? "col-12 list_map feature-list"
          : "col-md-6 col-lg-6 ml10 mr10"
      } take-tour-srp`}
      key={`card-item-${item?.propertyId}`}
      id={item?.propertyId}
      style={{
        width: isRecommendedProperties ? "auto" : isGridOrList ? "100%" : 350,
      }}
    >
      <div
        className={`feat_property home7 style4 ${
          isGridOrList ? "d-flex align-items-center" : ""
        } ${
          selectedProperty?.propertyId === item?.propertyId
            ? "selected-highlight"
            : ""
        }`}
      >
        <div
          className={`thumb cursor-pointer mt0 mb0 ml0 mr0 ${
            isGridOrList ? "srp-card-border-list" : "srp-card-border-grid"
          }`}
        >
          <ImageCarousel
            onClick={() => onNavigatePDP()}
            item={item}
            isGridOrList={isGridOrList}
            isRecommendedProperties={isRecommendedProperties}
          />
          <div className="thmb_cntnt" onClick={() => onNavigatePDP()}>
            <PropertyTags
              {...item}
              isRecommendedProperties={isRecommendedProperties}
            />

            <FavoriteButton
              item={item}
              cardIndex={cardIndex}
              setShowModal={setShowModal}
              isRecommendedProperties={isRecommendedProperties}
              properties={properties}
              setProperties={setProperties}
            />
            {!isGridOrList && (
              <div
                className="listing_list_style text-align-left p0 m0"
                style={{ zIndex: 2 }}
              >
                {Object.keys(tags).map((tagName) => {
                  if (tags[tagName]) {
                    return (
                      <div
                        className="list-inline-item p0 m0 srp-tooltip"
                        key={`tag-item-${tagName}`}
                        style={{ margin: 0 }}
                      >
                        <TagIcon tagName={tagName} />
                        <span className="srp-tooltiptext">
                          {tagStyle[tagName]?.title}
                          <span className="srp-tooltipArrow"></span>
                        </span>
                      </div>
                    );
                  }
                })}
                {
                  item?.propertyStatus === "PACT" && (
                    <span
                      className={`text-capitalize ${
                        !(tags?.length > 0)
                          ? "preview-tag"
                          : "preview-tag-single"
                      }`}
                    >
                      PREVIEW
                    </span>
                  )}
              </div>
            )}
          </div>
        </div>
        <FavoriteModal
          showModal={showModal}
          setShowModal={setShowModal}
          handleClose={() => setShowModal(false)}
          item={item}
        />
        <div className="details">
          <div
            className="tc_content pt0"
            style={{
              marginTop: isGridOrList ? 0 : 7,
              padding: 18,
              paddingBottom: 8,
              minHeight: isRecommendedProperties ? 134 : isGridOrList ? 160 : "auto",
              maxHeight: isRecommendedProperties ? 134 : isGridOrList ? 180 : 170,
            }}
          >
            <div className={`row ${isGridOrList ? "pt2" : ""}`}>
              <div className={isGridOrList ? "col-md-8" : "col-md-12"}>
                <TitleHeader
                  item={item}
                  isGridOrList={isGridOrList}
                  onNavigatePDP={() => onNavigatePDP()}
                />
                {item?.commercialRetailTag === "Y" && item?.shortDescription ? (
                  <div>
                    <span
                      className={`card-item-short-description ${
                        isRecommendedProperties
                          ? "number_of_line_one"
                          : isGridOrList ? "number_of_line_two" : "number_of_line_one"
                      }`}
                    >
                      {item?.shortDescription}
                    </span>
                  </div>
                ) : item?.commercialRetailTag === "Y" ||
                  item?.isCommercialProperty === "Y" ? (
                  <div className="pt20" />
                ) : (
                  <BedBathSqft item={item} />
                )}
                {/* Add this condition (after the commercial retail tag logic) */}
                {item?.category === "COMMERCIAL" &&
                  item?.propertyStatus === "PACT" && (
                    <>
                      <div>
                        {item?.listingStartDate && (
                          <span className="auction-date-label">
                            Starts: {formatAuctionDate(item.listingStartDate)}
                          </span>
                        )}
                        <span className="auctionTimeLeft_heading">
                          Time Until Auction Begins:
                        </span>
                      </div>
                      <CountdownTimer
                        until={item?.auctionTimeLeft / 1000}
                        isRecommendedProperties={isRecommendedProperties}
                      />
                    </>
                  )}
                {item?.propertyStatus === "PACT" &&
                item?.category !== "COMMERCIAL" &&
                  item?.previewPeriodEndAt !== null && (
                    <>
                      <div>
                        {item?.previewPeriodEndAt && (
                          <span className="auction-date-label">
                            Starts: {formatAuctionDate(item.listingStartDate)}
                          </span>
                        )}
                        <span className="auctionTimeLeft_heading">
                          Time Until Auction Begins:
                        </span>
                      </div>
                      <CountdownTimer
                        until={(item?.previewPeriodEndAt || 0) / 1000}
                        isRecommendedProperties={isRecommendedProperties}
                      />
                    </>
                  )}
                {item?.category !== "FORECLOSURE" &&
                  item?.propertyStatus == "" &&
                  item?.isPreview && (
                    <div
                      className="cursor-pointer"
                      style={{ marginBottom: -20, marginTop: 20 }}
                    >
                      <span className="preview-prop-desc number_of_line_two">
                        Hubzu does not guarantee preview properties will be
                        available for bids or offers.
                      </span>
                    </div>
                  )}
                {item?.category === "FORECLOSURE" &&
                (item?.propertyStatus === "ACTV" || item?.propertyStatus === "ACTIVE") &&
                item?.fcOnlineFlag === "Y" ? (
                  hasValidCountdown && (
                    <>
                      <div>
                        <span className="auctionTimeLeft_heading">
                          Auction Ends:
                        </span>
                        {item?.listingEndDate && (
                          <span className="auction-date-label">
                            {formatAuctionDate(item.listingEndDate)}
                          </span>
                        )}
                      </div>
                      <CountdownTimer
                        until={item?.auctionTimeLeft / 1000}
                        isRecommendedProperties={isRecommendedProperties}
                      />
                    </>
                  )
                ) : item?.category === "FORECLOSURE" &&
                  item?.fcOnlineFlag === "Y" &&
                  (item?.propertyStatus === "WFA" ||
                    item?.propertyStatus === "POSTPONED") ? (
                  <ul
                    className={`prop_details ${
                      isRecommendedProperties ? "pb10" : "pb30"
                    } pt15`}
                  >
                    {item?.propertyStatus === "WFA" ? (
                      <li className="list-inline-item">
                        <div className="card-item-head">
                          Scheduled{" "}
                          {item?.auctionDuration
                            ? `for ${item?.auctionDuration}`
                            : ""}
                        </div>
                      </li>
                    ) : (
                      <li className="list-inline-item">
                        <div className="card-item-head">
                          Auction Event Postponed{" "}
                          {item?.auctionDuration
                            ? `to ${item?.auctionDuration}`
                            : ""}
                        </div>
                      </li>
                    )}
                    {!isRecommendedProperties && (
                      <li className="list-inline-item">
                        <div className="srp-county-name">
                          <span>COUNTY: </span>
                          {item?.propAddress?.county || ""}
                        </div>
                      </li>
                    )}
                  </ul>
                ) : item?.category === "FORECLOSURE" &&
                  item?.fcOnlineFlag === "Y" &&
                  item?.propertyStatus &&
                  item?.propertyStatus === "WLST" &&
                  item?.listingStatus &&
                  item?.listingStatus === "Closed" &&
                  item?.listingDetailStatus &&
                  item?.listingDetailStatus === "CANCELLED" ? (
                  <div
                    className="cursor-pointer"
                    style={{ marginBottom: -20, marginTop: 20 }}
                  >
                    <span className="card-item-head number_of_line_one">
                      Auction Event Cancelled
                    </span>
                  </div>
                ) : item?.category === "FORECLOSURE" &&
                  item?.fcOnlineFlag === "Y" ? (
                  <div
                    className="cursor-pointer"
                    style={{ marginBottom: -20, marginTop: 20 }}
                  >
                    <span className="card-item-head number_of_line_one">
                      Auction Event Ended
                    </span>
                  </div>
                ) : item?.category === "FORECLOSURE" ? (
                  <>
                    {item?.category === "FORECLOSURE" &&
                    (item?.propertyStatus === "SCHEDULED" ||
                      item?.propertyStatus === "CLEAR_TO_SALE") ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          {item?.propertyStatus === "SCHEDULED"
                            ? "Scheduled"
                            : "Cleared to sell"}{" "}
                          {item?.event
                            ? `for ${
                                item?.event.eventDate +
                                ", " +
                                item?.event.eventTime +
                                " local"
                              }`
                            : ""}
                        </span>
                      </div>
                    ) : item?.category === "FORECLOSURE" &&
                      item?.propertyStatus === "POSTPONED" ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          Postponed
                        </span>
                      </div>
                    ) : item?.category === "FORECLOSURE" &&
                      item?.propertyStatus === "AUCTION_EVENT_ENDED" ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          Auction Ended
                        </span>
                      </div>
                    ) : item?.category === "FORECLOSURE" &&
                      item?.propertyStatus === "EXPIRED" ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          Auction Ended
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                    {!isRecommendedProperties &&
                      item?.event &&
                      (item?.propertyStatus === "SCHEDULED" ||
                        item?.propertyStatus === "CLEAR_TO_SALE") && (
                        <ul
                          className={`prop_details ${
                            isRecommendedProperties ? "pb10" : "pb30"
                          } pt25`}
                        >
                          <li className="list-inline-item">
                            <div className="srp-county-name">
                              <span>COUNTY: </span>
                              {item?.event?.venueDetails?.county || ""}
                            </div>
                          </li>
                        </ul>
                      )}
                  </>
                ) : item?.listingType === "TRNL" &&
                  item?.category != "FORECLOSURE" ? (
                  <div
                    className="cursor-pointer"
                    style={{ marginBottom: -20, marginTop: 20 }}
                  >
                    <span className="card-item-head number_of_line_one">
                      Traditional Sale
                    </span>
                  </div>
                ) : item?.category != "FORECLOSURE" &&
                  (item?.propertyStatus === "ACTV" || item?.propertyStatus === "ACTIVE")? (
                  hasValidCountdown && (
                    <>
                      <div >
                        <span className="auctionTimeLeft_heading">
                          Auction Ends:
                        </span>
                        {item?.listingEndDate && (
                          <span className="auction-date-label">
                            {formatAuctionDate(item.listingEndDate)}
                          </span>
                        )}
                      </div>
                      <CountdownTimer
                        until={item?.auctionTimeLeft / 1000}
                        isRecommendedProperties={isRecommendedProperties}
                      />
                    </>
                  )
                ) : item?.isPreview != true ? (
                  <>
                    {item?.category != "FORECLOSURE" &&
                    item?.propertyStatus === "WFA" ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          Scheduled{" "}
                          {item?.auctionDuration
                            ? `for ${item?.auctionDuration}`
                            : ""}
                        </span>
                      </div>
                    ) : item?.tagName === "UnderContract" ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          Auction Ended
                        </span>
                      </div>
                    ) : item?.tagName === "UnderReview" ? (
                      <div
                        className="cursor-pointer"
                        style={{ marginBottom: -20, marginTop: 20 }}
                      >
                        <span className="card-item-head number_of_line_one">
                          Under Review
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </div>
              {isGridOrList && (
                <div className="card-right-side-tags">
                  {Object.keys(tags).map((tagName) => {
                    if (tags[tagName]) {
                      return (
                        <div
                          className="cursor-pointer mb10"
                          style={{ justifyItems: "flex-end" }}
                          key={`tags-grid-${tagName}`}
                        >
                          <div
                            className="card-item-tag number_of_line_one"
                            style={{
                              color: tagStyle[tagName]?.color,
                              borderColor: tagStyle[tagName]?.color,
                            }}
                            tabIndex="0"
                          >
                            {tagStyle[tagName]?.title}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              )}
            </div>
            {!hasValidCountdown &&
              !(
                item?.propertyStatus === "WFA" && item?.auctionDuration != null
              ) &&
              ((!isRecommendedProperties &&
                item?.category === "FORECLOSURE" &&
                item?.event?.eventDate) ||
              item?.listingType === "TRNL" ||
              (item?.commercialRetailTag === "Y" && item?.shortDescription) ? (
                <div className="min_auction_height"></div>
              ) : (
                <div className="min_head_title_height" />
              ))}
          </div>
          <div
            className={`fp_footer srp-card-footer ${
              isRecommendedProperties ? "" : isGridOrList ? "list" : "grid"
            }`}
          >
            <PriceAndLabel item={item} />
            <CardBidButton
              isRecommendedProperties={isRecommendedProperties}
              item={item}
              cardIndex={cardIndex}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(CardItem);
